import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Star, MapPin, Users, Trophy, X, Crown } from "lucide-react";
import api from "../lib/api";
import { bannerFor } from "../components/ListingCard";
import { useMobile } from "../hooks/useMobile";
import { MobilePartnerBanner } from "../components/MobilePartnerBanner";

const rankBg = (i) => (i === 0 ? "#FCD34D" : i === 1 ? "#E5E7EB" : i === 2 ? "#FDBA74" : "#fff");

export default function Rankings() {
  const [params, setParams] = useSearchParams();
  const mobile = useMobile();
  const [list, setList] = useState([]);
  const [points, setPoints] = useState(null);
  const [meta, setMeta] = useState({ cities: [], areas: [], exams: [] });
  const filters = { city: params.get("city") || "", area: params.get("area") || "", type: params.get("type") || "", exam: params.get("exam") || "" };

  useEffect(() => {
    api.get("/points/public").then((r) => setPoints(r.data)).catch(() => {});
    api.get("/institutions/cities").then((r) => setMeta({ cities: [], areas: [], exams: [], ...r.data })).catch(() => {});
  }, []);

  useEffect(() => {
    const q = Object.fromEntries([...params.entries()].filter(([k, v]) => v && ["city", "area", "type", "exam"].includes(k)));
    api.get("/institutions", { params: q }).then((r) => setList(r.data)).catch(() => {});
  }, [params]);

  const update = (k, v) => {
    const next = { ...filters, [k]: v };
    setParams(Object.fromEntries(Object.entries(next).filter(([, val]) => val)));
  };
  const activeCount = Object.values(filters).filter(Boolean).length;

  return (
    <div className="rankings-page max-w-4xl mx-auto px-4 sm:px-8 py-12">
      <p className="nb-label" style={{ letterSpacing: "0.2em" }}>Leaderboard</p>
      <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight mt-2">
        {filters.exam ? `Top institutes for ${filters.exam}` : mobile ? "Top Performers" : "Institute Rankings"}
      </h1>
      {mobile && <div className="mobile-directory-tabs mt-5" data-testid="rank-mobile-categories">{[["", "All Institutes"], ["coaching", "Coaching"], ["school", "Schools"], ["college", "Colleges"]].map(([key, label]) => <button key={key} data-testid={`rank-category-${key || 'all'}`} aria-pressed={filters.type === key} onClick={() => update("type", key)}>{label}</button>)}</div>}
      <p className="rankings-explanation text-slate-600 mt-3">
        {mobile && <span className="block font-medium text-[#263043] mb-1">100% Algorithmic & Transparent · All-time rankings</span>}
        Powered by reviews ({points?.review_points ?? 10} pts), referrals ({points?.referral_points ?? 100} pts),
        admissions ({points?.admission_points ?? 5} pts) and post shares ({points?.share_post_points ?? 10} pts).
      </p>

      <div className="rankings-filter-grid mt-8 grid grid-cols-2 md:grid-cols-4 gap-3">
        <select data-testid="rank-city-filter" value={filters.city} onChange={(e) => update("city", e.target.value)} className="nb-input">
          <option value="">All cities</option>
          {meta.cities.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select data-testid="rank-area-filter" value={filters.area} onChange={(e) => update("area", e.target.value)} className="nb-input">
          <option value="">All areas</option>
          {meta.areas.map((a) => <option key={a} value={a}>{a}</option>)}
        </select>
        <select data-testid="rank-type-filter" value={filters.type} onChange={(e) => update("type", e.target.value)} className="nb-input">
          <option value="">All categories</option>
          <option value="coaching">Coaching</option>
          <option value="school">School</option>
          <option value="college">College</option>
        </select>
        <select data-testid="rank-exam-filter" value={filters.exam} onChange={(e) => update("exam", e.target.value)} className="nb-input">
          <option value="">All exams</option>
          {meta.exams.map((x) => <option key={x} value={x}>{x}</option>)}
        </select>
      </div>
      {activeCount > 0 && (
        <button data-testid="rank-clear-filters" onClick={() => setParams({})} className="mt-3 text-xs font-bold flex items-center gap-1 text-slate-500 hover:text-red-600">
          <X className="w-3.5 h-3.5" /> Clear {activeCount} filter{activeCount > 1 ? "s" : ""}
        </button>
      )}

      <div className="mt-8 space-y-5">
        {list.length === 0 && (
          <div data-testid="rankings-empty" className="nb-flat p-12 text-center border-dashed text-slate-500">No institutions match these filters yet.</div>
        )}
        {list.length >= 3 && (
          <div data-testid="rankings-podium" className="grid grid-cols-3 gap-2 sm:gap-4 items-end pb-4">
            {[list[1], list[0], list[2]].map((inst, pos) => {
              const place = pos === 1 ? 1 : pos === 0 ? 2 : 3;
              const bg = place === 1 ? "#FCD34D" : place === 2 ? "#E5E7EB" : "#FDBA74";
              return (
                <Link key={inst.id} to={`/institutes/${inst.id}`} data-testid={`podium-${place}`}
                  className={`nb p-3 sm:p-5 text-center flex flex-col items-center ${place === 1 ? "pb-6 sm:pb-8 -mt-4" : ""}`} style={{ background: place === 1 ? "#FFFBEB" : "#fff" }}>
                  <Crown className={`${place === 1 ? "w-7 h-7 text-amber-500" : "w-5 h-5 text-slate-400"}`} />
                  <img src={bannerFor(inst)} alt={inst.name} className={`${place === 1 ? "w-20 h-20" : "w-14 h-14"} rounded-2xl object-cover border-2 border-[#171B33] mt-2`} />
                  <p className="font-display font-extrabold text-xs sm:text-base mt-2 line-clamp-2 leading-tight">{inst.name}</p>
                  <span className="nb-badge mt-2" style={{ background: bg, padding: "2px 10px" }}><Star className="w-3 h-3 fill-current" /> {Math.round(inst.total_score)}</span>
                  <p className="text-[10px] uppercase tracking-widest text-slate-400 font-bold mt-1">#{place}</p>
                </Link>
              );
            })}
          </div>
        )}
        {mobile && list.length >= 3 && <h2 className="font-medium text-lg mt-5" data-testid="rank-contenders">Contenders <span className="text-xs bg-white px-2 py-1 rounded-md ml-2">Rank 4 onwards</span></h2>}
        {(mobile && list.length >= 3 ? list.slice(3) : list).map((inst, idx) => {
          const i = mobile && list.length >= 3 ? idx + 3 : idx;
          return (
          <Link key={inst.id} to={`/institutes/${inst.id}`} data-testid={`ranking-row-${inst.id}`}
            className="nb flex items-center gap-5 p-4 hover:-translate-y-0.5 transition-transform duration-150 fade-up"
            style={{ animationDelay: `${Math.min(i * 50, 400)}ms` }}>
            <span className="nb-flat flex-shrink-0 w-14 h-12 flex items-center justify-center font-display font-extrabold" style={{ background: rankBg(i), borderRadius: 12 }}>
              #{i + 1}
            </span>
            <img src={bannerFor(inst)} alt={inst.name} className="hidden sm:block w-14 h-12 object-cover nb-flat" style={{ borderRadius: 10 }} />
            <div className="min-w-0 flex-1">
              <p className="font-display font-extrabold truncate">{inst.name}</p>
              <div className="flex items-center gap-3 text-xs text-slate-500 mt-1 flex-wrap">
                <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {inst.area}</span>
                <span className="flex items-center gap-1"><Star className="w-3 h-3 fill-amber-400 text-amber-400" /> {inst.avg_rating || "—"}</span>
                <span className="flex items-center gap-1"><Users className="w-3 h-3" /> {inst.admissions || 0}+</span>
                {(inst.exams || []).slice(0, 3).map((x) => <span key={x} className="nb-badge" style={{ padding: "1px 8px", fontSize: 10, background: "#F2FBE3" }}>{x}</span>)}
              </div>
            </div>
            <div className="text-right flex-shrink-0">
              <p className="font-display text-xl font-extrabold flex items-center gap-1.5 justify-end">
                <Trophy className="w-4 h-4 text-orange-500" /> {Math.round(inst.total_score)}
              </p>
              <p className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">Points</p>
            </div>
          </Link>
        ); })}
      </div>
      {mobile && <div className="mt-7"><MobilePartnerBanner testId="ranking-partner-banner" /></div>}
    </div>
  );
}
